import React, {createContext, useContext, useEffect, useState} from 'react';
import {useFetchHook} from "../hooks/useFetchHook";
import fetcher from "../fetcher";
import {useParams} from "react-router";
import useDidMountEffect from "../hooks/useDidMountEffect";
import {toast} from "react-toastify";

export const PURCHASE_DEAL_LIGHT = '#1BA897'
export const PURCHASE_DEAL_DARK = '#007D6E'
export const LEASE_DEAL_DARK = '#2267CE'
export const LEASE_DEAL_LIGHT = '#2B8EEA'

export const DeskingContext = createContext(undefined)

export const DeskingContextProvider = ( { children, ...props } ) => {
    const [deskingState, setDeskingState] = useState(null)
    
    const [fetchDealResponse, fetchDeal] = useFetchHook(fetcher.getDeal)
    const [createDealResponse, createDeal] = useFetchHook(fetcher.createDeal)
    const [updateDealResponse, updateDeal] = useFetchHook(fetcher.updateDeal)
    const [fetchDeskingResponse, fetchDesking] = useFetchHook(fetcher.getDesking)

    const [selectedTerm, setSelectedTerm] = useState(props.selectedTerm)
    const [selectedLender, setSelectedLender] = useState(props.selectedLender)
    const [selectedTier, setSelectedTier] = useState(props.selectedTier)
    // program_code uniquely identifies the deal's selected program — used to
    // highlight exactly that quote in the grid (term/lender/tier alone over-match).
    const [selectedProgramCode, setSelectedProgramCode] = useState(props.selectedProgramCode)

    const dealColorDark = () => {
        return deskingState?.is_purchase ? PURCHASE_DEAL_DARK : LEASE_DEAL_DARK
    }

    const dealColorLight = () => {
        return deskingState?.is_purchase ? PURCHASE_DEAL_LIGHT : LEASE_DEAL_LIGHT
    }

    const dealColorDarkAlt = () => {
        return deskingState?.is_purchase ? LEASE_DEAL_DARK : PURCHASE_DEAL_DARK
    }

    const dealColorLightAlt = () => {
        return deskingState?.is_purchase ? LEASE_DEAL_LIGHT : PURCHASE_DEAL_LIGHT
    }

    const setDeskingStateWithUpdate = (newState, shouldFetchNew = false) => {
        setDeskingState({...deskingState, ...newState, _should_fetch: shouldFetchNew })
    }

    useEffect(function fetchDeskingFromProps(){
        fetchDesking(props)
    }, [])

    useEffect(function setFetchedDeskingQuote(){
        if (fetchDeskingResponse?.data?.data) {
            // The chosen tax region is request-only — the response carries it on
            // formatted_quote, never at the top level — so it is set from props
            // wherever deskingState is rebuilt from a response. Both writes need
            // it: updateDeskingStateFromBackend below replaces the state
            // outright, so seeding it once would not survive. Without it the
            // create payload loses the user's choice and the deal prices at
            // MarketScan's auto-selected (highest) rate.
            setDeskingStateWithUpdate({
                ...fetchDeskingResponse?.data?.data,
                tax_region_id: props.tax_region_id ?? null,
            })
        }
    }, [fetchDeskingResponse])

    useDidMountEffect(function fetchNewDeskingOnStateChange(){
        if (deskingState?._should_fetch === true) {
            const newDeskingState = structuredClone(deskingState)
            delete newDeskingState.lender
            fetchDesking(newDeskingState)
        }
    }, [deskingState])

    useDidMountEffect(function updateDeskingStateFromBackend(){
        if (fetchDeskingResponse?.data?.data && !fetchDeskingResponse.loading && !fetchDeskingResponse.error) {
            const newDeskingState = fetchDeskingResponse?.data?.data
            // TEMP DIAGNOSTIC — remove. Dumps the distinct raw `tier` values the
            // backend sends, with their JS type, to confirm where "T53" comes from.
            const rawTiers = (newDeskingState?.quotes || []).map((q) => `${typeof q.tier}:${JSON.stringify(q.tier)}`)
            setDeskingState({
                ...newDeskingState,
                tax_region_id: props.tax_region_id ?? null,
            })
        }
    }, [fetchDeskingResponse])


    const value = {
        deskingState,
        setDeskingState,

        fetchDesking,
        fetchDeskingResponse,

        updateDeal,
        updateDealResponse,

        fetchDeal,
        fetchDealResponse,

        createDeal,
        createDealResponse,

        setDeskingStateWithUpdate,

        dealColorDark,
        dealColorLight,
        dealColorDarkAlt,
        dealColorLightAlt,

        ...props,

        selectedTerm,
        setSelectedTerm,

        selectedLender,
        setSelectedLender,

        selectedTier,
        setSelectedTier,

        selectedProgramCode,
        setSelectedProgramCode,
    }

    return (
        <DeskingContext.Provider value={value}>
            { children }
        </DeskingContext.Provider>
    )
}

export class NoDeskingContextError extends Error {
    constructor(message) {
        super(message);
        this.name = 'NoDealContextError';
    }
}

export const useDeskingContext = () => {
    const context = useContext(DeskingContext)

    if (context === undefined) {
        throw new NoDeskingContextError('useDeskingContext must be used within DeskingContext')
    }
    return context
}